import React from 'react'
import { z } from 'zod'

import { Button } from '../../catalyst/button'
import { ErrorMessage, Field, Label } from '../../catalyst/fieldset'
import { Heading } from '../../catalyst/heading'
import { Strong, Text, TextLink } from '../../catalyst/text'
import { Logo } from '../../components/Logo'
import { LodgeSelect } from '../../components/LodgeSelect'
import {
  ConsentField,
  schemas,
  TextField,
  useNativeFormSubmit,
  useValidatedForm,
} from '../../components/forms'

interface RegisterProps {
  actionUrl: string
  signInUrl: string
  authenticity_token: string
  lodgeSearchUrl: string
  termsUrl: string
  privacyUrl: string
  flash?: Record<string, string>
}

const MIN_PASSWORD_LENGTH = 6

// Nested to mirror the params hash Rails receives; see `submitName` in
// components/forms/fields.
const registerSchema = z
  .object({
    user: z.object({
      first_name: schemas.personName('First name'),
      last_name: schemas.personName('Last name'),
      email: schemas.email(),
      password: z
        .string()
        .min(1, 'Password is required.')
        .min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`),
      password_confirmation: z.string().min(1, 'Please confirm your password.'),
      lodge_id: z.number({ message: 'Select your lodge.' }).nullable(),
      terms_of_service: schemas.requiredCheckbox('You must accept the terms to create an account.'),
    }),
  })
  .superRefine((data, ctx) => {
    const { password, password_confirmation: confirmation, lodge_id: lodgeId } = data.user

    if (confirmation && confirmation !== password) {
      ctx.addIssue({
        code: 'custom',
        message: 'Passwords do not match.',
        path: ['user', 'password_confirmation'],
      })
    }

    if (lodgeId === null) {
      ctx.addIssue({ code: 'custom', message: 'Select your lodge.', path: ['user', 'lodge_id'] })
    }
  })

type RegisterValues = z.infer<typeof registerSchema>

/** Names accept letters and the punctuation they really contain, nothing else. */
const stripNonName = (value: string) => value.replace(/[^\p{L}\s'’-]/gu, '')

export function Register({
  actionUrl,
  signInUrl,
  authenticity_token,
  lodgeSearchUrl,
  termsUrl,
  privacyUrl,
  flash,
}: RegisterProps) {
  const form = useValidatedForm<RegisterValues>(registerSchema, {
    defaultValues: {
      user: {
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        lodge_id: null,
        terms_of_service: false,
      },
    },
  })
  const formProps = useNativeFormSubmit(form)

  const lodgeError = form.formState.errors.user?.lodge_id?.message

  return (
    <div className="w-full max-w-sm">
      <form
        action={actionUrl}
        method="POST"
        className="grid w-full grid-cols-1 gap-6"
        {...formProps}
      >
        <input type="hidden" name="authenticity_token" value={authenticity_token} />

        {/* Honeypot: hidden from real visitors, catches simple bots. Left unnamed under `user[...]` so it can't collide with a real field. */}
        <div
          aria-hidden="true"
          className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden"
        >
          <label htmlFor="website">Leave this field blank</label>
          <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" />
        </div>

        <div>
          <a href="/" aria-label="Home">
            <Logo className="mx-auto h-12 w-auto text-zinc-950 dark:text-white" />
          </a>
          <Heading className="mt-6 text-center">Join your lodge</Heading>
          <div className="mt-3 rounded-md bg-zinc-50 dark:bg-zinc-800/50 px-4 py-3 text-center text-sm text-zinc-600 dark:text-zinc-300">
            Search for your lodge below to get started. Not sure which one?{' '}
            <a href="/find-a-lodge" className="underline font-medium">
              Find your lodge here
            </a>
            .
          </div>
        </div>

        {flash?.alert && (
          <div
            role="alert"
            className="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/50 dark:text-red-200"
          >
            {flash.alert}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <TextField
            form={form}
            name="user.first_name"
            submitName="user[first_name]"
            label="First Name"
            autoComplete="given-name"
            maxLength={50}
            transform={stripNonName}
          />
          <TextField
            form={form}
            name="user.last_name"
            submitName="user[last_name]"
            label="Last Name"
            autoComplete="family-name"
            maxLength={50}
            transform={stripNonName}
          />
        </div>

        <TextField
          form={form}
          name="user.email"
          submitName="user[email]"
          label="Email"
          type="email"
          autoComplete="email"
        />

        <TextField
          form={form}
          name="user.password"
          submitName="user[password]"
          label="Password"
          type="password"
          autoComplete="new-password"
          description={`Minimum ${MIN_PASSWORD_LENGTH} characters.`}
        />

        <TextField
          form={form}
          name="user.password_confirmation"
          submitName="user[password_confirmation]"
          label="Confirm password"
          type="password"
          autoComplete="new-password"
        />

        <hr className="my-2 border-zinc-200 dark:border-zinc-700" />

        <div>
          <h3 className="mb-2 text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            Find Your Lodge
          </h3>
          <Field>
            <Label>Search by name or number</Label>
            <div className="py-2">
              <LodgeSelect
                searchUrl={lodgeSearchUrl}
                name="user[lodge_id]"
                invalid={!!lodgeError}
                onSelectionChange={(lodgeId) =>
                  form.setValue('user.lodge_id', lodgeId, {
                    shouldValidate: true,
                    shouldTouch: true,
                  })
                }
              />
            </div>
            {lodgeError ? (
              <ErrorMessage>{lodgeError}</ErrorMessage>
            ) : (
              <Text className="mt-1 text-sm">
                Select your existing lodge. Accounts are reviewed by lodge admins.
              </Text>
            )}
          </Field>
        </div>

        <ConsentField
          form={form}
          name="user.terms_of_service"
          submitName="user[terms_of_service]"
          label={
            <>
              I agree to the{' '}
              <a
                href={termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href={privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                Privacy Policy
              </a>
              .
            </>
          }
        />

        <div className="mt-4">
          <Button type="submit" className="w-full">
            Create Account
          </Button>
        </div>

        <Text className="text-center">
          Already have an account?{' '}
          <TextLink href={signInUrl}>
            <Strong>Sign in</Strong>
          </TextLink>
        </Text>
      </form>
    </div>
  )
}
